import { type ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { toaster } from '@/shared/ui/chakra/toaster';
import { useAdminEventStream } from '@/shared/lib/sse/useEventStream';
import type { SseEvent } from '@/shared/lib/sse/parser';
import {
  LuBlocks,
  LuBuilding2,
  LuClipboardList,
  LuFileText,
  LuFuel,
  LuIdCard,
  LuLayoutDashboard,
  LuLibrary,
  LuMap,
  LuMapPin,
  LuMessageSquare,
  LuShieldCheck,
  LuSmartphone,
  LuTag,
  LuTimer,
  LuTriangleAlert,
  LuUserCog,
  LuUsers,
} from 'react-icons/lu';
import { Avatar } from '@/shared/ui/chakra/avatar';
import { LogoMark } from '@/shared/ui/Logo';
import { ColorModeButton, useColorModeValue } from '@/shared/ui/chakra/color-mode';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/shared/ui/chakra/menu';
import { useAuthStore } from '@/shared/lib/auth/store';
import { hasPermission } from '@/shared/lib/auth/permissions';
import { chatApi } from '@/features/chat/api';
import { stuckDeliveriesApi } from '@/features/stuck-deliveries/api';
import { logout } from '@/features/auth/api/logout';
import { useLivePermissionSync } from '@/features/auth/model/useLivePermissionSync';
import { useInstallPrompt } from '@/features/pwa-install/useInstallPrompt';
import { NotificationsToggle } from '@/features/notifications/ui/NotificationsToggle';
import { WebauthnCredentialsModal } from '@/features/auth/ui/WebauthnCredentialsModal';
import { ResponsiveShell } from '@/shared/ui/shell/ResponsiveShell';
import type { ShellNavItem, ShellNavSection } from '@/shared/ui/shell/types';
import { shouldSignalChat } from './chat-signal';

/* App layout shown on every authenticated page: a fixed sidebar with
   icon nav + a 64px top bar carrying the theme toggle, user identity,
   and an account menu. Sub-pages render into <Outlet/>. */

interface NavItem {
  to: string;
  i18nKey: string;
  icon: ComponentType<{ size?: number | string }>;
  /** Etap 35 — hidden unless the current admin's `permission_codes` grants this (or
   *  `full_access`). Absent = always shown (dashboard/map aren't gated to one permission). */
  permission?: string;
}

/** Short WebAudio chime for an incoming operator message. Best-effort: if the
    browser blocks autoplay before a user gesture, or lacks AudioContext, it
    silently no-ops. A cue the dispatcher can HEAR when the tab is backgrounded,
    where the toast/badge are invisible. */
let audioCtx: AudioContext | null = null;
function playChime(): void {
  try {
    const Ctor = window.AudioContext;
    if (!Ctor) return;
    audioCtx ??= new Ctor();
    const ctx = audioCtx;
    // If the context was created while suspended (no prior user gesture), it
    // self-heals once activation exists; resume() is a no-op when running.
    void ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
  } catch {
    // autoplay blocked / unsupported — the visual cues still fire
  }
}

/**
 * Global "new dispatcher chat activity" signal. Outside /chat there was no indication a message
 * arrived — the badge on the "Чат" nav item + a toast + a chime cover that. The badge is
 * SERVER-DERIVED (summed unread from GET /v1/admin/chats), not a local counter, so it survives
 * reloads, reflects partial reads, reconciles on reconnect, and never under-reports messages that
 * arrived while the tab was away. See [[project_pwa_webpush]] for the OS-level background path.
 */
function useChatSignal(): number {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const [unread, setUnread] = useState(0);
  const inFlight = useRef(false);
  const pending = useRef(false);
  const refetchRef = useRef<() => void>(() => {});

  // Server-authoritative unread total. Every path that could change it
  // (mount, focus, route change, live event, SSE reconnect) re-derives from here.
  // Coalesced: only one inbox fetch is ever in flight; if triggers arrive during
  // it, exactly one more runs afterward. This removes both the request storm (a
  // burst of N messages → 1 fetch, not N) and out-of-order writes (only one
  // response is ever outstanding, so a stale response can't clobber a newer one).
  const refetchUnread = useCallback(async () => {
    if (inFlight.current) {
      pending.current = true;
      return;
    }
    inFlight.current = true;
    try {
      const rows = await chatApi.inbox();
      setUnread(rows.reduce((sum, r) => sum + (r.unread ?? 0), 0));
    } catch {
      // best-effort — keep the last known value rather than resetting to 0
    } finally {
      inFlight.current = false;
      if (pending.current) {
        pending.current = false;
        void refetchRef.current();
      }
    }
  }, []);
  refetchRef.current = refetchUnread;

  // Hydrate on mount + whenever the tab regains focus (a wake/tab-switch may
  // have missed live events) or a thread is marked read on /chat (ChatPage
  // dispatches `chat:read`, which the shell badge can't see otherwise).
  useEffect(() => {
    void refetchUnread();
    const onWake = () => void refetchUnread();
    window.addEventListener('focus', onWake);
    window.addEventListener('chat:read', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      window.removeEventListener('chat:read', onWake);
    };
  }, [refetchUnread]);

  // Re-derive on route change: after reading threads on /chat and navigating
  // away, the server unread drops — the badge must follow (not hard-reset to 0).
  useEffect(() => {
    void refetchUnread();
  }, [pathname, refetchUnread]);

  // Flash the tab title with the unread count while the tab is backgrounded, so
  // a dispatcher looking at another window still sees something waiting.
  useEffect(() => {
    const base = t('common.app_name');
    const update = () => {
      document.title = unread > 0 && document.hidden ? `(${unread}) ${base}` : base;
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      document.title = base;
    };
  }, [unread, t]);

  const onEvent = useCallback(
    (ev: SseEvent) => {
      const { signal, isApproval } = shouldSignalChat(ev);
      if (!signal) return;
      // Reconcile the badge from the server on any qualifying event.
      void refetchUnread();
      // Perceptible cue for the message itself — but not while the dispatcher
      // is already looking at the chat page.
      if (pathRef.current === '/chat') return;
      toaster.create({
        title: isApproval ? t('chat.new_approval_title') : t('chat.new_message_title'),
        type: 'info',
      });
      playChime();
    },
    [t, refetchUnread],
  );
  // Resync on (re)connect too — fills any gap the 60s replay buffer missed.
  useAdminEventStream(onEvent, () => void refetchUnread());

  return unread;
}

/* Как часто пересчитывается значок «незавершённые операции».
 *
 * Не по событию: строка попадает в тупик молча — её туда кладёт фоновый воркер, исчерпав попытки,
 * и никакого события админке при этом не приходит. Раз в пять минут достаточно: деньги там висят
 * часами, а не секундами, и лишний опрос в минуту ничего не спасёт. */
const STUCK_POLL_MS = 5 * 60 * 1000;

/* Сколько операций лежит незавершёнными — цифра рядом с пунктом меню.
 *
 * Без неё экран мёртв: никто не открывает раздел, в котором обычно пусто, а узнать о зависшем
 * холде надо раньше, чем позвонит клиент. */
function useStuckSignal(enabled: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const read = async () => {
      try {
        const data = await stuckDeliveriesApi.list({ limit: 1 });
        if (!cancelled) setCount(data.total);
      } catch {
        // Молча: значок — подсказка, а не задача. Упасть из-за него шапка не должна.
      }
    };
    void read();
    const timer = setInterval(() => void read(), STUCK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled]);

  return count;
}

const NAV_SECTIONS: ReadonlyArray<readonly NavItem[]> = [
  [
    { to: '/', i18nKey: 'nav.dashboard', icon: LuLayoutDashboard },
    { to: '/map', i18nKey: 'nav.map', icon: LuMap },
    { to: '/orders', i18nKey: 'nav.orders', icon: LuClipboardList, permission: 'manage_orders' },
  ],
  [
    { to: '/operators', i18nKey: 'nav.operators', icon: LuUsers, permission: 'manage_operators' },
    {
      to: '/providers',
      i18nKey: 'nav.providers',
      icon: LuBuilding2,
      permission: 'manage_providers',
    },
    { to: '/clients', i18nKey: 'nav.clients', icon: LuIdCard, permission: 'manage_clients' },
    { to: '/catalog', i18nKey: 'nav.catalog', icon: LuLibrary, permission: 'manage_catalog' },
    {
      to: '/consumers',
      i18nKey: 'nav.consumers',
      icon: LuSmartphone,
      permission: 'manage_consumers',
    },
    {
      to: '/consumer-zones',
      i18nKey: 'nav.consumer_zones',
      icon: LuMapPin,
      permission: 'manage_catalog',
    },
    { to: '/pricing', i18nKey: 'nav.pricing', icon: LuTag, permission: 'manage_pricing' },
    {
      to: '/personal-car-settings',
      i18nKey: 'nav.personal_car_settings',
      icon: LuTimer,
      permission: 'manage_catalog',
    },
    {
      to: '/stuck-deliveries',
      i18nKey: 'nav.stuck_deliveries',
      icon: LuTriangleAlert,
      permission: 'manage_orders',
    },
  ],
  [
    {
      to: '/refill-requests',
      i18nKey: 'nav.refill_requests',
      icon: LuFuel,
      permission: 'manage_refill_requests',
    },
    { to: '/chat', i18nKey: 'nav.chat', icon: LuMessageSquare, permission: 'manage_chat' },
    { to: '/reports', i18nKey: 'nav.reports', icon: LuFileText, permission: 'view_reports' },
    {
      to: '/reports/builder',
      i18nKey: 'nav.report_builder',
      icon: LuBlocks,
      permission: 'view_reports',
    },
  ],
  [
    { to: '/admins', i18nKey: 'nav.admins', icon: LuUserCog, permission: 'manage_admins' },
    {
      to: '/admin-roles',
      i18nKey: 'nav.admin_roles',
      icon: LuShieldCheck,
      permission: 'manage_roles',
    },
  ],
];

/** Цифра рядом с пунктом меню: непрочитанные сообщения у чата, незавершённые операции у их
    экрана. Остальным пунктам значок не положен. */
function badgeFor(to: string, chatUnread: number, stuckCount: number): number {
  if (to === '/chat') return chatUnread;
  if (to === '/stuck-deliveries') return stuckCount;
  return 0;
}

/** Curated subset of NAV_SECTIONS for the mobile bottom tab bar — Главная/Карта have no
    permission gate, so every role sees at least these two (the bar only renders past 2 items;
    see ResponsiveShell). Routes only, not full item defs, to derive from NAV_SECTIONS instead of
    redeclaring icon/i18nKey/permission a second time. */
const TAB_BAR_ROUTES = ['/', '/map', '/orders', '/chat'];

export function AppShell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const router = useRouter();
  const logoTone = useColorModeValue<'brand' | 'onDark'>('brand', 'onDark');
  const chatUnread = useChatSignal();
  const stuckCount = useStuckSignal(hasPermission(user?.permission_codes, 'manage_orders'));
  // The map wants every pixel — drop the content padding on its route (the map fills edge-to-edge;
  // its own fullscreen toggle then covers the shell entirely). Other pages keep the roomy p="6".
  const isMapRoute = useRouterState({ select: (s) => s.location.pathname === '/map' });
  // Session-only (not persisted) — matches the reference pattern in nafta-report's Sidebar.
  const [collapsed, setCollapsed] = useState(false);
  // Navigating INTO /map auto-collapses the sidebar to its icon rail for more map; leaving restores
  // the pre-map preference. Only on the client-side transition (not on a direct load/refresh of
  // /map) — so the user can still manually re-expand while on the map, and a fresh /map load keeps
  // the default rail. `wasMapRoute` seeds from the first render, so a direct /map load is a no-op.
  const wasMapRoute = useRef(isMapRoute);
  const preMapCollapsed = useRef(false);
  useEffect(() => {
    if (isMapRoute === wasMapRoute.current) return;
    wasMapRoute.current = isMapRoute;
    if (isMapRoute) {
      preMapCollapsed.current = collapsed;
      setCollapsed(true);
      return;
    }
    setCollapsed(preMapCollapsed.current);
  }, [isMapRoute, collapsed]);
  // Etap 35 slice 6: live nav/page-guard sync — see the hook's doc comment for why this can't
  // just be "re-fetch /v1/me on mount".
  useLivePermissionSync();
  const { canInstall, promptInstall } = useInstallPrompt();
  const [webauthnModalOpen, setWebauthnModalOpen] = useState(false);
  const visibleSections = NAV_SECTIONS.map((section) =>
    section.filter(
      (item) => !item.permission || hasPermission(user?.permission_codes, item.permission),
    ),
  ).filter((section) => section.length > 0);

  const toShellItem = (item: NavItem): ShellNavItem => ({
    key: item.to,
    to: item.to,
    label: t(item.i18nKey),
    icon: item.icon,
    badge: badgeFor(item.to, chatUnread, stuckCount),
    // `/reports` is exact so it doesn't stay lit on its `/reports/builder` sibling.
    exact: item.to === '/' || item.to === '/reports',
  });
  const shellSections: ShellNavSection[] = visibleSections.map((section, idx) => ({
    key: `section-${idx}`,
    items: section.map(toShellItem),
  }));
  const allItems = NAV_SECTIONS.flat();
  const tabItems: ShellNavItem[] = TAB_BAR_ROUTES.map((to) =>
    allItems.find((item) => item.to === to),
  )
    .filter((item): item is NavItem => Boolean(item))
    .filter((item) => !item.permission || hasPermission(user?.permission_codes, item.permission))
    .map(toShellItem);

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      reset();
      await router.navigate({ to: '/login' });
    }
  };

  return (
    <ResponsiveShell
      brand={
        <Flex align="center" gap="2.5" px="2" justify={collapsed ? 'center' : 'flex-start'}>
          <LogoMark size={26} tone={logoTone} />
          {!collapsed && (
            <Heading size="md" color="colorPalette.fg">
              {t('common.app_name')}
            </Heading>
          )}
        </Flex>
      }
      sidebarAriaLabel={t('layout.sidebar_label')}
      sections={shellSections}
      tabItems={tabItems}
      menuTabLabel={t('layout.menu')}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((v) => !v)}
      collapseLabels={{
        collapse: t('layout.collapse_sidebar'),
        expand: t('layout.expand_sidebar'),
      }}
      headerContent={
        <>
          <NotificationsToggle />
          <ColorModeButton />
          <Text fontSize="sm" color="fg.muted" display={{ base: 'none', md: 'block' }}>
            {user ? `${user.username} · ${user.role_name ?? user.role}` : ''}
          </Text>
          <MenuRoot>
            <MenuTrigger asChild>
              <Box as="button" aria-label={t('common.account')} cursor="pointer" rounded="full">
                <Avatar size="sm" name={user?.username ?? ''} />
              </Box>
            </MenuTrigger>
            <MenuContent>
              {canInstall && (
                <MenuItem value="install" onClick={() => void promptInstall()}>
                  {t('pwa.install')}
                </MenuItem>
              )}
              <MenuItem value="webauthn" onClick={() => setWebauthnModalOpen(true)}>
                {t('webauthn.manage_title')}
              </MenuItem>
              <MenuItem value="sign-out" onClick={handleSignOut}>
                {t('common.sign_out')}
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </>
      }
      mobileMenuLabel={t('layout.open_menu')}
      mobileMenuTitle={t('common.app_name')}
      edgeToEdge={isMapRoute}
    >
      <Outlet />
      <WebauthnCredentialsModal
        open={webauthnModalOpen}
        onClose={() => setWebauthnModalOpen(false)}
      />
    </ResponsiveShell>
  );
}
